// Screen C - the public trust page's AUTHED MANAGEMENT UI (docs/design/
// slice4-certify-ux.md §6). Route: /apps/:appId/certify/trust-page. This screen never
// renders the public page itself - the canonical public HTML render is a separate
// `apps/api` server route (ADR-0020 decision 5, `GET /v1/trust/:slug`); this screen
// owns only the publish/unpublish control, the founder's authed PREVIEW, and a link to
// the canonical public URL.
//
// LOAD-BEARING (D46, structural, never conflated): `programmaticReadiness` (the
// self-assessment) and `verifiedCredential` (a real, verified upload) render in TWO
// separate, independently-labelled containers below - never merged into one card or
// status line, even when both concern the same framework (§2.5, §6.2).
import { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { isErr } from "@nightcrew/shared";
import type { FrameworkKey } from "@nightcrew/shared";
import type { AppDetailContext } from "../AppDetail.js";
import {
  getTrustPageManagement,
  publicTrustPageUrl,
  publishTrustPage,
  unpublishTrustPage,
} from "../../api/certify.js";
import type { TrustPageManagementResponse } from "../../api/certify.js";
import { isOwnerOrAdmin, useSession } from "../../state/session.js";
import { evaluateCertifyGate } from "../../certify/gate.js";
import { certifyCopy, type CertifyCopyKey } from "../../copy/certify.js";
import { CertifyTierRefusalCard } from "../../components/CertifyTierRefusalCard.js";
import { LifecycleGateCard } from "../../components/LifecycleGateCard.js";
import { SelfAssessmentDisclosure } from "../../components/SelfAssessmentDisclosure.js";
import { CertifiedBadge } from "../../components/CertifiedBadge.js";

const FRAMEWORK_LABELS: Record<FrameworkKey, string> = {
  gdpr: "GDPR",
  soc2: "SOC 2",
  iso27001: "ISO 27001",
  nis2: "NIS2",
  hipaa: "HIPAA",
};

/**
 * W4 fix (design-review, PUBLIC-page severity): `TrustClaim.source` is a free-form wire
 * token shaped `<kind>:<key>` (e.g. "scan:secret_scan", "assessment:gdpr-2026.1" -
 * packages/shared/src/model/certify.ts:251, apps/web/src/testing/certify-fixtures.ts:
 * 150,164), never itself human copy. This screen's preview - what a founder's PUBLIC
 * trust page would show - was rendering it verbatim. Maps the token's `<kind>` prefix to
 * a human label; an unrecognized prefix falls back to a generic label rather than ever
 * leaking the raw token (fail closed on an unmapped/future source kind).
 */
const CLAIM_SOURCE_LABEL_KEY: Record<string, CertifyCopyKey> = {
  scan: "certify.trustPage.claimSource.scan",
  assessment: "certify.trustPage.claimSource.assessment",
  config: "certify.trustPage.claimSource.config",
};

const claimSourceLabel = (source: string): string => {
  const prefix = source.split(":")[0] ?? "";
  const key = CLAIM_SOURCE_LABEL_KEY[prefix];
  return certifyCopy(key ?? "certify.trustPage.claimSource.fallback");
};

type ViewState =
  | { readonly status: "loading" }
  | { readonly status: "error" }
  | { readonly status: "loaded"; readonly data: TrustPageManagementResponse };

export const TrustPageManagement = (): React.JSX.Element => {
  const { appId, app } = useOutletContext<AppDetailContext>();
  const { state: session } = useSession();
  const account = session.status === "ready" ? session.account : null;
  const canManage = isOwnerOrAdmin(session);
  const gate = evaluateCertifyGate(app, account);
  const entitled = !gate.tierDenied && !gate.stageDenied;

  const [view, setView] = useState<ViewState>({ status: "loading" });
  const [attempt, setAttempt] = useState(0);
  const [featuredFramework, setFeaturedFramework] = useState<FrameworkKey | null>(null);
  const [featuredCertRef, setFeaturedCertRef] = useState<string | null>(null);
  const [includeClaims, setIncludeClaims] = useState(true);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!entitled) return;
    const cancelled = { current: false };
    setView({ status: "loading" });
    void (async () => {
      const result = await getTrustPageManagement(appId);
      if (cancelled.current) return;
      if (isErr(result)) {
        setView({ status: "error" });
        return;
      }
      setView({ status: "loaded", data: result.value });
      setFeaturedFramework(result.value.featuredFramework ?? result.value.eligibleFrameworks[0] ?? null);
      setFeaturedCertRef(result.value.verifiedCertificateRefs[0] ?? null);
    })();
    return () => {
      cancelled.current = true;
    };
  }, [entitled, appId, attempt]);

  if (gate.tierDenied) {
    return (
      <div className="wrap">
        <CertifyTierRefusalCard appName={app.name} stageDenied={gate.stageDenied} onAskAboutUpgrading={() => undefined} />
      </div>
    );
  }
  if (gate.stageDenied) {
    return (
      <div className="wrap">
        <LifecycleGateCard appName={app.name} />
      </div>
    );
  }
  if (view.status === "loading") {
    return (
      <div className="wrap">
        <div className="card skeleton-card" />
      </div>
    );
  }
  if (view.status === "error") {
    return (
      <div className="wrap">
        <div className="card state-card">
          <p>That didn&apos;t go through. Try again?</p>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => {
              setAttempt((a) => a + 1);
            }}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  const { data } = view;

  if (!data.hasCertifyResults) {
    return (
      <div className="wrap">
        <h1 className="h2">{certifyCopy("certify.trustPage.management.heading")}</h1>
        <div className="card state-card mt16">
          <p className="muted">{certifyCopy("certify.trustPage.management.notReadyBody")}</p>
        </div>
      </div>
    );
  }

  const publicUrl = data.publicSlug !== null ? publicTrustPageUrl(data.publicSlug) : null;

  const handlePublish = (): void => {
    setBusy(true);
    void (async () => {
      const result = await publishTrustPage(appId, {
        featuredFramework,
        featuredCertificateRef: featuredCertRef,
        includeClaims,
      });
      setBusy(false);
      setConfirming(false);
      if (isErr(result)) return;
      setView({
        status: "loaded",
        data: { ...data, published: true, publicSlug: result.value.publicSlug, featuredFramework },
      });
    })();
  };

  const handleUnpublish = (): void => {
    setBusy(true);
    void (async () => {
      const result = await unpublishTrustPage(appId);
      setBusy(false);
      if (isErr(result)) return;
      setView({ status: "loaded", data: { ...data, published: false, publicSlug: null } });
    })();
  };

  return (
    <div className="wrap">
      <h1 className="h2">{certifyCopy("certify.trustPage.management.heading")}</h1>

      {data.published && publicUrl !== null ? (
        <div className="card pad mt16" data-testid="trust-page-published">
          <span className="status-chip good">{certifyCopy("certify.trustPage.management.publishedStatus")}</span>
          <p className="mt8" style={{ fontSize: 13 }}>
            <span className="muted">{certifyCopy("certify.trustPage.management.publicUrlLabel")}: </span>
            <a href={publicUrl} target="_blank" rel="noreferrer">
              {publicUrl}
            </a>
          </p>
          {canManage ? (
            <div className="actions mt12">
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                onClick={() => {
                  navigator.clipboard.writeText(publicUrl).catch(() => undefined);
                }}
              >
                {certifyCopy("certify.trustPage.management.copyUrlCta")}
              </button>
              <button type="button" className="btn btn-ghost btn-sm" disabled={busy} onClick={handleUnpublish}>
                {certifyCopy("certify.trustPage.management.unpublishCta")}
              </button>
            </div>
          ) : null}
        </div>
      ) : (
        <div className="mt16" data-testid="trust-page-draft">
          <span className="chip" data-testid="preview-watermark">
            {certifyCopy("certify.trustPage.management.previewBadge")}
          </span>
        </div>
      )}

      {data.preview !== null ? (
        <div className="stack gap16 mt16" data-testid="trust-page-preview">
          <div className="card pad row aic gap12">
            <CertifiedBadge state={data.preview.badge} appName={app.name} />
            <span className="muted" style={{ fontSize: 12 }}>
              {certifyCopy("certify.trustPage.nav.lastChecked", {
                lastCheckedDate: new Date(data.preview.lastVerifiedAt).toLocaleDateString(),
              })}
            </span>
          </div>

          {/* Programmatic readiness - its OWN container, never merged with the
              verified-credential block below (§2.5). */}
          <div className="card pad" data-testid="preview-readiness">
            {/* W5 fix (design-review): this exact label ("{Framework} readiness") also
                renders in FrameworkResultCard.tsx:87 as `<h3 className="h3">` (sans, DM
                Sans - brand.css's `h1,h2{font-family:Fraunces}` rule applies by ELEMENT,
                not by the `.h3` class, and `.h3` sets no font-family of its own). This
                was an `<h2 className="h3">` here, so the same string rendered serif on
                the trust preview and sans on the dashboard. Matched to `<h3>` so one
                label is one font wherever it appears. Scoped to only this heading
                (the sibling headings below render different, non-duplicated labels, out
                of this fix's scope) - flagged: this does shift local heading depth from
                h1->h2 to h1->h3 on this screen; a wrapping h2 section heading would
                close that gap cleanly if the design/accessibility review wants it. */}
            <h3 className="h3">
              {featuredFramework !== null
                ? certifyCopy("certify.dashboard.frameworkResult.heading", {
                    frameworkName: FRAMEWORK_LABELS[featuredFramework],
                  })
                : "Readiness"}
            </h3>
            <div className="mt12">
              <SelfAssessmentDisclosure length="long" appName={app.name} />
            </div>
            {data.preview.programmaticReadiness.provisional ? (
              <p className="status-chip warn mt12" data-testid="provisional-flag">
                {certifyCopy("certify.dashboard.frameworkResult.provisional")}
              </p>
            ) : null}
            <ul className="mt12" style={{ margin: 0, paddingInlineStart: 18 }}>
              {data.preview.programmaticReadiness.claims.map((claim, i) => (
                <li key={i} style={{ fontSize: 13 }}>
                  {claim.value}
                  {/* W4 (source, humanized) + W6 (11px -> 12px, the established
                      neighboring-caption step on this same page, e.g. line 227 above and
                      the verified-credential caption below - never below the 12px
                      body-text floor). */}
                  <span className="muted" style={{ fontSize: 12 }}>
                    {" "}
                    - {claimSourceLabel(claim.source)}, {new Date(claim.checkedAt).toLocaleDateString()}
                  </span>
                </li>
              ))}
            </ul>
          </div>

          {/* Verified credential - a SEPARATE, distinctly-labelled container (§2.5) -
              never sharing a border/background/heading with the readiness card above,
              even when both concern the same framework. */}
          {data.preview.verifiedCredential !== null ? (
            <div className="well pad" data-testid="preview-verified-credential">
              <h2 className="h3">A verified certificate is on file</h2>
              <p className="mt8" style={{ fontSize: 13 }}>
                {data.preview.verifiedCredential.issuer} - {data.preview.verifiedCredential.scope}
              </p>
              <p className="muted" style={{ fontSize: 12 }}>
                Verified {new Date(data.preview.verifiedCredential.verifiedAt).toLocaleDateString()}
              </p>
            </div>
          ) : (
            <p className="muted" style={{ fontSize: 12.5 }} data-testid="no-verified-credential">
              No real certificate on file for this framework yet.
            </p>
          )}
        </div>
      ) : null}

      {!data.published && canManage ? (
        <div className="card pad mt16" data-testid="compose-picker">
          <h2 className="h3">{certifyCopy("certify.trustPage.management.chooseFeaturedFramework")}</h2>
          <select
            className="field mt8"
            value={featuredFramework ?? ""}
            onChange={(e) => {
              setFeaturedFramework(e.target.value === "" ? null : (e.target.value as FrameworkKey));
            }}
          >
            <option value="">{certifyCopy("certify.trustPage.management.noneOption")}</option>
            {data.eligibleFrameworks.map((fw) => (
              <option key={fw} value={fw}>
                {FRAMEWORK_LABELS[fw]}
              </option>
            ))}
          </select>
          <p className="muted mt8" style={{ fontSize: 11.5 }}>
            {certifyCopy("certify.trustPage.management.notEligibleNote")}
          </p>

          {data.verifiedCertificateRefs.length > 0 ? (
            <>
              <h2 className="h3 mt16">{certifyCopy("certify.trustPage.management.chooseFeaturedCertificate")}</h2>
              <select
                className="field mt8"
                value={featuredCertRef ?? ""}
                onChange={(e) => {
                  setFeaturedCertRef(e.target.value === "" ? null : e.target.value);
                }}
              >
                <option value="">{certifyCopy("certify.trustPage.management.noneOption")}</option>
                {data.verifiedCertificateRefs.map((ref) => (
                  <option key={ref} value={ref}>
                    {ref}
                  </option>
                ))}
              </select>
            </>
          ) : null}

          <label className="row aic gap8 mt16">
            <input
              type="checkbox"
              checked={includeClaims}
              onChange={(e) => {
                setIncludeClaims(e.target.checked);
              }}
            />
            Include the general assurance statements
          </label>

          {confirming ? (
            <div className="well pad mt16" data-testid="publish-confirm">
              <p style={{ fontSize: 13 }}>
                {certifyCopy("certify.trustPage.management.publishConfirmBody", {
                  subset: featuredFramework !== null ? FRAMEWORK_LABELS[featuredFramework] : "your general posture",
                  url: `${window.location.origin}/v1/trust/...`,
                })}
              </p>
              <div className="actions mt8">
                <button type="button" className="btn btn-primary" disabled={busy} onClick={handlePublish}>
                  {certifyCopy("certify.trustPage.management.publishConfirmCta")}
                </button>
                <button
                  type="button"
                  className="btn btn-ghost"
                  onClick={() => {
                    setConfirming(false);
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <button
              type="button"
              className="btn btn-primary mt16"
              onClick={() => {
                setConfirming(true);
              }}
            >
              {certifyCopy("certify.trustPage.management.publishCta")}
            </button>
          )}
        </div>
      ) : null}
    </div>
  );
};
